// The class moves on screen (CLASSES.md): each class's signature verb gets its own look, so a posse can tell
// who did what from across the island and the move itself feels like a power, not a position change.
//
//   Phantom    WARP: he folds out of the world (violet sparks sucked into the body, a crystal pop and a
//              tear of light where he stood), a jagged seam of light along the line, and he unfolds at the far
//              end (a ground ring, shards, a flare). The same fold plays at both ends of the blink strike.
//              CLOAK: violet smoke curls off a near-invisible body while it lasts.
//   Valkyrie   FIRE DASH: a sonic ring of fire where she kicks off and a wake of flame tongues and embers along
//              the line; the ground pound already gets its slam (Ev.Pound).
//   Juggernaut CHARGE: a wall of hex shield light carried in front of him, a shock ring at the kick-off, dust
//              thrown from his boots and sparks off the plate; robots he meets get the Stagger Quake as usual.
//   Gunslinger has no class move (her pistol whip and roll are her defence); Hot Shot's loaded bullet gets a
//              brass glint at the gun hand while it's chambered.
//
// Everything goes through the shared particle and card pools (no meshes, no lights, no new materials), so a
// class move costs no draw calls and can never cause a shader compile mid-fight.
import type { CowboyRS } from '../contracts';
import type { Fx } from './index';
import { PShape, PFlag } from './particles';
import { CMode, COrient, CFlag } from './cards';
import { linRGB } from './gpu';

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const C = {
  white: linRGB(0xfff6e4),
  violet: linRGB(0xb98cff), violetDeep: linRGB(0x6a3fd0), violetHot: linRGB(0xf2e6ff), cyan: linRGB(0x9ef3ff),
  smokeViolet: linRGB(0x5b4a86),
  flameHot: linRGB(0xffb83a), flameCool: linRGB(0xe8461c), fireCore: linRGB(0xfff0c0), ember: linRGB(0xff7a2a),
  blue: linRGB(0x6fb8ff), blueHot: linRGB(0xd6efff), steel: linRGB(0x9aa6b2), amber: linRGB(0xffb347),
  tan: linRGB(0xc9a877), smokeDark: linRGB(0x3a2a24), dirt: linRGB(0x8a6a44), brass: linRGB(0xffd27a),
};
const VIOLET_FLASH = [0.75, 0.55, 1.0];
const FIRE_FLASH = [1.0, 0.6, 0.3];
const BLUE_FLASH = [0.6, 0.8, 1.0];

const MOVE_DODGE = 4, MOVE_SPECIAL = 5;
const CHEST = 1.05;

interface Track { cloaked: boolean; cls: number; yaw: number; x: number; y: number; z: number; move: number; seen: number; warpAt: number; wx: number; wy: number; wz: number; trail: number; wisp: number; special: number }

export class ClassFx {
  private tracks = new Map<number, Track>();
  private time = 0;
  private streakN = 0;
  constructor(private fx: Fx) {}

  private n(k: number) { return Math.max(1, Math.round(k * this.fx.budget)); }
  private track(id: number) {
    let t = this.tracks.get(id);
    if (!t) { t = { cloaked: false, cls: -1, yaw: 0, x: 0, y: 0, z: 0, move: 0, seen: -1, warpAt: -99, wx: 0, wy: 0, wz: 0, trail: 0, wisp: 0, special: -99 }; this.tracks.set(id, t); }
    return t;
  }

  /** A class move started: the local cowboy's from prediction (no wait for the server), the rest from Ev.Special.
   *  (x, y, z) is where he was standing when it fired, if known; else the last place this lane saw him. */
  special(id: number, cls: number, local: boolean, x?: number, y?: number, z?: number, yaw?: number) {
    const t = this.track(id);
    if (cls < 0) cls = t.cls;
    if (this.time - t.special < 0.12) return; // the predicted start and the server's echo are one move
    t.special = this.time;
    const sx = x ?? t.x, sy = y ?? t.y, sz = z ?? t.z;
    const yw = yaw ?? t.yaw, fx = -Math.sin(yw), fz = -Math.cos(yw);
    if (cls === 2) {
      // the warp: fold out here; the far end unfolds once the body shows up there (update)
      this.warpOut(sx, sy, sz, local);
      t.warpAt = this.time; t.wx = sx; t.wy = sy; t.wz = sz;
    } else if (cls === 1) this.fireKick(sx, sy, sz, fx, fz, local);
    else if (cls === 3) this.ramKick(sx, sy, sz, fx, fz, local);
  }

  /** The Phantom's blink strike (Ev.Blink): the same fold, from where he stood to behind the robot. */
  blink(fromX: number, fromY: number, fromZ: number, toX: number, toY: number, toZ: number, local: boolean) {
    this.warpOut(fromX, fromY, fromZ, local, 0.6);
    this.seam(fromX, fromY + CHEST, fromZ, toX, toY + CHEST, toZ);
    this.warpIn(toX, toY, toZ, local, 1.25);
  }

  /** Each frame, over every cowboy drawn: trails for the moves that last, the warp's far end, the cloak's smoke. */
  update(list: readonly CowboyRS[], n: number, dt: number, time: number) {
    this.time = time;
    for (let i = 0; i < n; i++) {
      const c = list[i];
      const t = this.track(c.id);
      const fresh = t.seen < 0 || time - t.seen > 0.5;
      const px = t.x, py = t.y, pz = t.z;
      t.x = c.x; t.y = c.y; t.z = c.z; t.seen = time; t.cls = c.cls; t.yaw = c.yaw;
      if (fresh || c.hidden || c.phase !== 0) { t.move = c.move; t.warpAt = -99; continue; }
      const moved = Math.hypot(c.x - px, c.z - pz);
      // the warp's far end: the first frame the body is somewhere else (a teleport lands in one tick), or a
      // short wait if it was a wall-pinched hop of a metre or two
      if (c.cls === 2 && t.warpAt > 0) {
        const away = Math.hypot(c.x - t.wx, c.z - t.wz);
        if (away > 1.2 || time - t.warpAt > 0.35) {
          if (away > 0.8) this.seam(t.wx, t.wy + CHEST, t.wz, c.x, c.y + CHEST, c.z);
          this.warpIn(c.x, c.y, c.z, c.isLocal, 1);
          t.warpAt = -99;
        }
      }
      // trails: dropped by distance so a fast move and a slow frame lay the same wake
      if (c.move === MOVE_SPECIAL && (c.cls === 1 || c.cls === 3) && moved > 0 && moved < 6) {
        t.trail += moved;
        const step = c.cls === 1 ? 0.3 : 0.4;
        const dx = c.x - px, dz = c.z - pz, ml = moved || 1;
        while (t.trail >= step) {
          t.trail -= step;
          const k = 1 - t.trail / ml; // where along this frame's move
          const x = px + dx * k, y = py + (c.y - py) * k, z = pz + dz * k;
          if (c.cls === 1) this.fireWake(x, y, z, dx / ml, dz / ml, c.isLocal);
          else this.ramWake(x, y, z, dx / ml, dz / ml, c.isLocal);
        }
      } else t.trail = 0;
      // the Phantom's ground dash (○): a thin violet afterimage of sparks, so it reads as his, not a roll
      if (c.cls === 2 && c.move === MOVE_DODGE && moved > 0.05 && moved < 6 && Math.random() < 0.8) this.dashGlint(c.x, c.y, c.z);
      // the cloak: smoke curling off him while it lasts (his teammates see it too: they need to know where he is)
      if (c.cloaked && !t.cloaked) this.cloakOn(c.x, c.y, c.z);
      t.cloaked = c.cloaked;
      if (c.cloaked) {
        t.wisp += dt;
        while (t.wisp > 0.04) { t.wisp -= 0.04; this.cloakWisp(c.x, c.y, c.z, c.isLocal); }
      } else t.wisp = 0;
      t.move = c.move;
    }
    if (this.tracks.size > 32) for (const [id, t] of this.tracks) if (time - t.seen > 5) this.tracks.delete(id);
  }

  // ------------------------------------------------------------------ the Phantom
  /** fold out: the light is pulled into the body, it pops like a crystal, and a tear of light hangs where he stood */
  private warpOut(x: number, y: number, z: number, local: boolean, s = 1) {
    const ls = local ? 0.55 : 1; // his own departure never outshines his arrival
    const P = this.fx.particles, Cd = this.fx.cards;
    const cy = y + CHEST;
    // implosion: sparks start on a shell around him and rush inward
    for (let i = 0; i < this.n(40); i++) {
      const a = Math.random() * 6.283, e = rnd(-0.9, 0.9), r = rnd(0.6, 1.2) * s;
      const ux = Math.cos(a) * Math.sqrt(1 - e * e), uy = e, uz = Math.sin(a) * Math.sqrt(1 - e * e);
      const v = r / 0.1;
      P.spawn(x + ux * r, cy + uy * r * 1.2, z + uz * r, -ux * v, -uy * v * 1.2, -uz * v, 0.1, rnd(0, 0.04), 0, 0, -1e5,
        PShape.Spark, PFlag.Add | PFlag.Stretch | PFlag.NoHaze, 0.05, 0.02, 0, i & 1 ? C.violet : C.cyan, 1, C.violetHot, 0.8, 2.4, 2.8);
    }
    // the pop: a crystal flash and a hard ring, then a vertical tear of light where the body was
    Cd.spawn(CMode.Shock, COrient.Billboard, x, cy, z, 0, 0, 0, 0.16, 0.1, 0.1, 0.6 * s, C.violet, 0.8, C.violetDeep, CFlag.Add, 0.06, 0, 0, 0, 2.2);
    Cd.spawn(CMode.Streak, COrient.Segment, x, y - 0.2, z, x, y + 2.6, z, 0.5, 0.13, 0.32, 0.02, C.violetHot, 1, C.violet, CFlag.Add, 1, 0, 0, 0, 2.8);
    Cd.spawn(CMode.Glow, COrient.Billboard, x, cy, z, 0, 0, 0, 0.3, 0.1, 0.3 * s * ls * ls, 0.15, C.violet, 0.3, C.violetDeep, CFlag.Add, 0, 0, 0, 0, 1.6);
    Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.12, 0.1, 0.2 * s * ls, 0.6 * s * ls, C.violetHot, 0.8, C.violet, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, 2.4);
    // a breath of violet smoke left behind
    for (let i = 0; i < this.n(6); i++) {
      P.spawn(x + rnd(-0.3, 0.3), y + rnd(0.3, 1.6), z + rnd(-0.3, 0.3), rnd(-0.4, 0.4), rnd(0.2, 0.7), rnd(-0.4, 0.4), rnd(0.7, 1.1), 0.14, -0.2, 2.2, -1e5,
        PShape.Smoke, PFlag.Add | PFlag.Soft, 0.35, 0.9, rnd(-1, 1), C.violet, 0.35, C.smokeViolet, 0, 1, 1);
    }
    if (local) this.fx.pulse(0.22, VIOLET_FLASH, 0.6, 0.1);
  }
  /** unfold: he snaps back into the world in a ring of violet light and a spray of shards */
  private warpIn(x: number, y: number, z: number, local: boolean, s: number) {
    const P = this.fx.particles, Cd = this.fx.cards;
    const cy = y + CHEST, gy = this.fx.groundAt(x, y + 0.5, z);
    const fl = gy > y - 3 ? gy : y;
    Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.12, 0, 0.3 * s, 1.4 * s, C.violetHot, 0.9, C.violet, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, 2.4);
    Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.1, 0.07, 0.2 * s, 0.9 * s, C.cyan, 0.8, C.violet, CFlag.Add, 4, Math.random(), 0, Math.random() * 6, 2.2);
    Cd.spawn(CMode.Glow, COrient.Billboard, x, cy, z, 0, 0, 0, 0.4, 0, 0.6 * s, 0.25, C.violet, 0.35, C.violetDeep, CFlag.Add, 0, 0, 0, 0, 1.6);
    Cd.spawn(CMode.Shock, COrient.Ground, x, fl + 0.06, z, 0, 0, 0, 0.4, 0, 0.3, 2.0 * s, C.violetHot, 1, C.violet, CFlag.Add, 0.1, 0, 0, 0, 2.2);
    Cd.spawn(CMode.Shock, COrient.Ground, x, fl + 1.2, z, 0, 0, 0, 0.3, 0.03, 0.3, 1.6 * s, C.violetHot, 0.7, C.violet, CFlag.Add, 0.05, 0, 0, 0, 2);
    // the body draws itself back in: a column of light that shrinks onto him
    Cd.spawn(CMode.Streak, COrient.Segment, x, y - 0.1, z, x, y + 2.6, z, 0.22, 0, 0.5, 0.04, C.violetHot, 1, C.violet, CFlag.Add, 1, 0, 0, 0, 2.4);
    for (let i = 0; i < this.n(14); i++) {
      P.spawn(x + rnd(-0.35, 0.35), fl + rnd(0, 0.4), z + rnd(-0.35, 0.35), 0, rnd(2.5, 5), 0, rnd(0.35, 0.6), rnd(0, 0.08), 0, 2, -1e5,
        PShape.Spark, PFlag.Add | PFlag.Stretch | PFlag.NoHaze, 0.05, 0.02, 0, C.cyan, 1, C.violet, 0.5, 2.4, 2);
    }
    for (let i = 0; i < this.n(18); i++) {
      const a = Math.random() * 6.283, v = rnd(5, 11);
      P.spawn(x, cy + rnd(-0.6, 0.6), z, Math.cos(a) * v, rnd(-1, 4), Math.sin(a) * v, rnd(0.35, 0.6), 0, 9, 1.8, fl,
        PShape.Shard, i & 1 ? PFlag.Add : 0, rnd(0.12, 0.26), 0.05, rnd(-14, 14), i & 1 ? C.violet : C.violetHot, 1, C.violetDeep, 0.6, i & 1 ? 2 : 1.2, 1);
    }
    if (local) this.fx.pulse(0.3, VIOLET_FLASH, 0.9, 0.15);
  }
  /** the seam: a jagged rip of light along the line he folded through, and motes left hanging in it */
  private seam(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
    const P = this.fx.particles, Cd = this.fx.cards;
    const d = Math.hypot(x1 - x0, y1 - y0, z1 - z0);
    if (d < 0.5) return;
    Cd.spawn(CMode.Arc, COrient.Segment, x0, y0, z0, x1, y1, z1, 0.4, 0, Math.min(1.2, 0.25 + d * 0.08), 0, C.violet, 1, C.violetDeep, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.6);
    Cd.spawn(CMode.Streak, COrient.Segment, x0, y0, z0, x1, y1, z1, 0.18, 0, 0.16, 0.02, C.violetHot, 1, C.violet, CFlag.Add, 1, 0, 0, 0, 2.2);
    const motes = this.n(Math.min(32, 6 + d * 3));
    for (let i = 0; i < motes; i++) {
      const k = Math.random();
      P.spawn(x0 + (x1 - x0) * k + rnd(-0.2, 0.2), y0 + (y1 - y0) * k + rnd(-0.5, 0.5), z0 + (z1 - z0) * k + rnd(-0.2, 0.2), rnd(-0.3, 0.3), rnd(0.1, 0.6), rnd(-0.3, 0.3),
        rnd(0.4, 0.8), k * 0.06, 0, 1.5, -1e5, PShape.Glow, PFlag.Add | PFlag.NoHaze, 0.12, 0.02, 0, C.violetHot, 1, C.violet, 0, 2, 1.5);
    }
  }
  private dashGlint(x: number, y: number, z: number) {
    this.fx.particles.spawn(x + rnd(-0.25, 0.25), y + rnd(0.3, 1.7), z + rnd(-0.25, 0.25), 0, rnd(0, 0.4), 0, rnd(0.25, 0.4), 0, 0, 1, -1e5,
      PShape.Glow, PFlag.Add, 0.1, 0.02, 0, C.violetHot, 0.9, C.violet, 0, 1.6, 1);
  }
  /** the cloak takes: a ring snaps in around him and a line of light runs down the body */
  private cloakOn(x: number, y: number, z: number) {
    const Cd = this.fx.cards;
    Cd.spawn(CMode.Shock, COrient.Billboard, x, y + CHEST, z, 0, 0, 0, 0.3, 0, 1.6, 0.1, C.violet, 0.9, C.cyan, CFlag.Add, 0.08, 0, 0, 0, 2);
    Cd.spawn(CMode.Streak, COrient.Segment, x, y + 2.2, z, x, y, z, 0.3, 0, 0.18, 0.02, C.cyan, 1, C.violet, CFlag.Add, 1, 0, 0, 0, 2.2);
  }
  /** cloak smoke: violet curls rising off the body, with the odd glint of light catching the edge of him */
  private cloakWisp(x: number, y: number, z: number, local: boolean) {
    const P = this.fx.particles;
    const a = Math.random() * 6.283, r = rnd(0.15, 0.4);
    P.spawn(x + Math.cos(a) * r, y + rnd(0.2, 1.8), z + Math.sin(a) * r, Math.cos(a) * 0.3, rnd(0.4, 0.9), Math.sin(a) * 0.3, rnd(0.6, 1.0), 0, -0.3, 1.6, -1e5,
      PShape.Smoke, PFlag.Add | PFlag.Soft | PFlag.Swirl, 0.16, rnd(0.7, 1.0), rnd(-1.5, 1.5), C.violet, local ? 0.35 : 0.5, C.smokeViolet, 0, 1, 1);
    if (Math.random() < 0.25) {
      P.spawn(x + Math.cos(a) * 0.3, y + rnd(0.4, 1.7), z + Math.sin(a) * 0.3, 0, 0.2, 0, 0.25, 0, 0, 1, -1e5,
        PShape.Flare, PFlag.Add | PFlag.NoHaze, 0.12, 0.02, 0, C.violetHot, 1, C.cyan, 0, 1.8, 1);
    }
  }

  // ------------------------------------------------------------------ the Valkyrie
  private fireKick(x: number, y: number, z: number, fx: number, fz: number, local: boolean) {
    const Cd = this.fx.cards, P = this.fx.particles;
    const cy = y + CHEST;
    // a sonic ring of fire behind her, square to the line, and a burst where her heels were
    Cd.spawn(CMode.Shock, COrient.Plane, x - fx * 0.4, cy, z - fz * 0.4, fx, 0, fz, 0.35, 0, 0.2, 2.4, C.flameHot, 1, C.flameCool, CFlag.Add, 0.12, 0, 0, 0, 2.4);
    Cd.spawn(CMode.Shock, COrient.Plane, x - fx * 0.9, cy, z - fz * 0.9, fx * 0.97, 0.26, fz * 0.97, 0.28, 0.06, 0.1, 1.5, C.fireCore, 1, C.flameHot, CFlag.Add, 0.08, 0, 0, 0, 2);
    Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.14, 0, 0.3, 1.5, C.fireCore, 1, C.flameCool, CFlag.Add, 5, Math.random(), 0, Math.random() * 6, 2.2);
    for (let i = 0; i < this.n(14); i++) {
      const a = Math.random() * 6.283, v = rnd(3, 7);
      P.spawn(x - fx * 0.3, cy + rnd(-0.6, 0.4), z - fz * 0.3, -fx * v + Math.cos(a) * 2, rnd(-0.5, 2), -fz * v + Math.sin(a) * 2, rnd(0.25, 0.45), 0, -1, 2.5, -1e5,
        PShape.Fire, PFlag.Add | PFlag.Up, rnd(0.3, 0.5), rnd(0.6, 0.9), 0, C.flameHot, 1, C.flameCool, 0.8, 1.8, 1.4);
    }
    // a scorch line where her heels left the ground
    const gy = this.fx.groundAt(x, y + 0.5, z), fl = gy > y - 2 ? gy : y;
    for (let i = 0; i < this.n(6); i++) {
      const k = i * 0.25;
      P.spawn(x - fx * k + rnd(-0.1, 0.1), fl + 0.1, z - fz * k + rnd(-0.1, 0.1), 0, 0.4, 0, rnd(0.5, 0.7), i * 0.02, 0, 2, -1e5,
        PShape.Fire, PFlag.Add, 0.4, 0.15, 0, C.flameHot, 0.8, C.flameCool, 0, 1.6, 1.1);
    }
    if (local) this.fx.pulse(0.18, FIRE_FLASH, 0.35, 0.12);
  }
  /** the wake: flame tongues licking up off her line and embers spun out behind */
  private fireWake(x: number, y: number, z: number, dx: number, dz: number, local: boolean) {
    const P = this.fx.particles;
    // the comet's tail: a hot streak along the step she just covered, cooling to red
    if (!local || (this.streakN++ & 1) === 0) this.fx.cards.spawn(CMode.Streak, COrient.Segment, x + (local ? dx * 0.3 : 0), y + 1.1, z + (local ? dz * 0.3 : 0), x - dx * 0.6, y + 1.0, z - dz * 0.6, 0.3, 0, 0.4, 0.08, local ? C.flameHot : C.fireCore, local ? 0.18 : 0.9, C.flameCool, CFlag.Add, 1, 0, 0, 0, 2.2);
    for (let i = 0; i < 3; i++) {
      // tongues licking off her line at hip height and below, swept back and shrinking (not a row of candles)
      P.spawn(x + rnd(-0.3, 0.3), y + rnd(0.2, 1.2), z + rnd(-0.3, 0.3), -dx * rnd(2, 4) + rnd(-0.5, 0.5), rnd(0.5, 1.6), -dz * rnd(2, 4) + rnd(-0.5, 0.5), rnd(0.4, 0.7), rnd(0, 0.03), -1.2, 2.5, -1e5,
        PShape.Fire, PFlag.Add, rnd(0.6, 0.9), rnd(0.25, 0.4), rnd(-3, 3), i ? C.flameCool : C.flameHot, 0.9, C.flameCool, 0.0, 1.9, 1.2);
    }
    P.spawn(x, y + rnd(0.4, 1.4), z, -dx * 3 + rnd(-1.5, 1.5), rnd(0.5, 3), -dz * 3 + rnd(-1.5, 1.5), rnd(0.5, 0.9), 0, 3, 1.4, -1e5,
      PShape.Spark, PFlag.Add | PFlag.Stretch, 0.05, 0.02, 0, C.ember, 1, C.ember, 0.3, 2.4, 1.4);
    P.spawn(x + rnd(-0.2, 0.2), y + rnd(0.6, 1.4), z + rnd(-0.2, 0.2), -dx * 0.6, rnd(0.5, 1.1), -dz * 0.6, rnd(0.9, 1.4), 0.12, -0.3, 1.8, -1e5, PShape.Smoke, PFlag.Soft, 0.35, rnd(0.9, 1.3), rnd(-0.5, 0.5), C.smokeDark, 0.4, C.smokeDark, 0, 1, 1);
  }

  // ------------------------------------------------------------------ the Juggernaut
  private ramKick(x: number, y: number, z: number, fx: number, fz: number, local: boolean) {
    const Cd = this.fx.cards;
    const cy = y + CHEST;
    const gy = this.fx.groundAt(x, y + 0.5, z), fl = gy > y - 2 ? gy : y;
    // a shock ring off his shoulders and a stamp of dust where he pushes off
    Cd.spawn(CMode.Shock, COrient.Plane, x + fx * 0.8, cy, z + fz * 0.8, fx, 0, fz, 0.3, 0.04, 0.3, 2.0, C.blueHot, 1, C.blue, CFlag.Add, 0.2, 0, 0, 0, 2.2);
    Cd.spawn(CMode.Quake, COrient.Ground, x, fl + 0.05, z, 0, 0, 0, 0.7, 0, 0.3, 2.2, C.dirt, 0.8, C.dirt, 0, 6, Math.random() * 50, 0, 0, 1.2);
    Cd.spawn(CMode.Quake, COrient.Ground, x, fl + 0.05, z, 0, 0, 0, 0.9, 0.05, 0.4, 3.0, C.blue, 0.4, C.blueHot, CFlag.Add, 6, Math.random() * 50, 0, 0, 1.4);
    const P = this.fx.particles;
    for (let i = 0; i < this.n(12); i++) {
      const a = Math.random() * 6.283, v = rnd(2, 5);
      P.spawn(x + Math.cos(a) * 0.5, fl + 0.3, z + Math.sin(a) * 0.5, Math.cos(a) * v - fx * 2, rnd(0.5, 2), Math.sin(a) * v - fz * 2, rnd(1.2, 1.6), 0, -0.4, 2.8, fl,
        PShape.Puff, 0, 0.4, 1.1, rnd(-0.5, 0.5), Math.random() < 0.5 ? C.tan : C.dirt, 1, C.dirt, 1, 1, 0.92);
    }
    if (local) this.fx.pulse(0.12, BLUE_FLASH, 0.3, 0.45);
  }
  /** the ram: a wall of hex shield light carried at his front, dust off his boots, sparks scraping off the plate */
  private ramWake(x: number, y: number, z: number, dx: number, dz: number, local: boolean) {
    const pa = local ? 0.2 : 1; // his own camera sits right behind the prow
    const Cd = this.fx.cards, P = this.fx.particles;
    const cy = y + CHEST;
    // a prow of shield light: two hex panes angled back from his front like a ram's bow, so it reads head-on
    // to the robots and side-on to his posse, with a hot glow where they meet
    const qx = -dz, qz = dx, k = 0.62; // the panes' normals: the line turned ±~40°
    for (const sd of [-1, 1]) {
      const nx = dx * 0.77 + qx * sd * 0.64, nz = dz * 0.77 + qz * sd * 0.64;
      Cd.spawn(CMode.Hex, COrient.Plane, x + dx * 0.9 + qx * sd * k, cy, z + dz * 0.9 + qz * sd * k, nx, 0, nz, 0.12, 0, 1.35, 1.5, C.blue, pa, C.blueHot, CFlag.Add, 0, 0, 0, Math.random() * 6, 2.1);
    }
    if (!local) Cd.spawn(CMode.Glow, COrient.Billboard, x + dx * 1.3, cy, z + dz * 1.3, 0, 0, 0, 0.14, 0, 1.0, 1.1, C.blueHot, 0.7 * pa, C.blue, CFlag.Add, 0, 0, 0, 0, 1.8);
    Cd.spawn(CMode.Shock, COrient.Plane, x + dx * 1.3, cy, z + dz * 1.3, dx, 0, dz, 0.12, 0, 1.0, 1.6, C.blueHot, 0.8 * pa, C.blue, CFlag.Add, 0.06, 0, 0, 0, 1.6);
    const fl = this.fx.groundAt(x, y + 0.5, z);
    const gy = fl > y - 2 ? fl : y;
    for (let i = 0; i < 2; i++) {
      P.spawn(x + rnd(-0.4, 0.4), gy + 0.2, z + rnd(-0.4, 0.4), -dx * rnd(1, 3) + rnd(-1.5, 1.5), rnd(0.6, 2.2), -dz * rnd(1, 3) + rnd(-1.5, 1.5), rnd(0.7, 1.2), 0, -0.3, 2.6, gy,
        PShape.Puff, 0, 0.35, rnd(0.9, 1.4), rnd(-0.5, 0.5), Math.random() < 0.5 ? C.tan : C.dirt, 1, C.dirt, 1, 1, 0.92);
    }
    const sa = Math.random() * 6.283;
    P.spawn(x + dx * 1.1 + Math.cos(sa) * 0.6, cy + rnd(-0.6, 0.6), z + dz * 1.1 + Math.sin(sa) * 0.6, -dx * rnd(3, 6) + Math.cos(sa) * 3, rnd(1, 4), -dz * rnd(3, 6) + Math.sin(sa) * 3,
      rnd(0.25, 0.45), 0, 12, 1.6, gy, PShape.Spark, PFlag.Add | PFlag.Stretch, 0.05, 0.02, 0, C.amber, 1, C.blueHot, 0.5, 2.6, 1.6);
  }

  // ------------------------------------------------------------------ the Gunslinger
  /** Hot Shot chambered: a brass glint at the gun hand, every so often while the window is open */
  hotShot(x: number, y: number, z: number) {
    if (Math.random() > 0.2) return;
    this.fx.particles.spawn(x, y, z, 0, 0.3, 0, 0.3, 0, 0, 1, -1e5, PShape.Flare, PFlag.Add | PFlag.NoHaze, 0.16, 0.03, 0, C.brass, 1, C.white, 0, 2.2, 1);
  }
}
